import { Shield, Clock, Trash2, Lock, Server, Eye } from "lucide-react";
import SEOHead from "@/components/SEOHead";

const PrivacyPolicyPage = () => {
  return (
    <>
      <SEOHead
        title="Privacy Policy | SwissFlow PDF"
        description="Learn how SwissFlow PDF handles your files and data. Files auto-delete after 30 minutes. No storage, no tracking. Your privacy is our priority."
        canonicalPath="/privacy-policy"
        keywords="privacy policy, PDF privacy, file security, data protection"
      />
      <div className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-12 animate-fade-in-up">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-[#FF3B30] flex items-center justify-center">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold font-['Outfit'] tracking-tight">
                Privacy Policy
              </h1>
            </div>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Your privacy is our priority. Learn how we handle your files and data.
            </p>
          </div>

        {/* Content */}
        <div className="space-y-8 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
          {/* Last Updated */}
          <p className="text-sm text-muted-foreground font-mono">
            Last updated: January 2026
          </p>

          {/* Key Points */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="bg-card border border-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <Clock className="w-5 h-5 text-[#FF3B30]" />
                <h3 className="font-semibold font-['Outfit']">Auto-Deletion</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                All uploaded files are automatically deleted after 30 minutes. No exceptions.
              </p>
            </div>

            <div className="bg-card border border-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <Server className="w-5 h-5 text-[#FF3B30]" />
                <h3 className="font-semibold font-['Outfit']">No Storage</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                We do not permanently store any of your documents on our servers.
              </p>
            </div>

            <div className="bg-card border border-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <Eye className="w-5 h-5 text-[#FF3B30]" />
                <h3 className="font-semibold font-['Outfit']">No Tracking</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                We don't track your file contents or personal document information.
              </p>
            </div>

            <div className="bg-card border border-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <Lock className="w-5 h-5 text-[#FF3B30]" />
                <h3 className="font-semibold font-['Outfit']">Secure Processing</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Files are processed securely and never shared with third parties.
              </p>
            </div>
          </div>

          {/* Detailed Sections */}
          <div className="space-y-6 mt-12">
            <section>
              <h2 className="text-2xl font-bold font-['Outfit'] mb-4">1. Information We Collect</h2>
              <div className="prose prose-sm text-muted-foreground space-y-3">
                <p>
                  <strong>Files You Upload:</strong> When you use our PDF tools, you upload files to our servers for processing. These files are temporarily stored only for the duration needed to complete your requested operation.
                </p>
                <p>
                  <strong>Technical Data:</strong> We may collect basic technical information such as browser type and general usage statistics to improve our service. This data is anonymized and cannot be used to identify you personally.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold font-['Outfit'] mb-4">2. How We Use Your Information</h2>
              <div className="prose prose-sm text-muted-foreground space-y-3">
                <p>Your uploaded files are used solely for the purpose of providing the PDF service you requested (conversion, merging, splitting, etc.).</p>
                <p>We do not:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Read or analyze the contents of your documents</li>
                  <li>Share your files with third parties</li>
                  <li>Use your files for training AI models</li>
                  <li>Create copies of your files beyond what's needed for processing</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold font-['Outfit'] mb-4">3. Data Retention</h2>
              <div className="prose prose-sm text-muted-foreground space-y-3">
                <p>
                  <strong>30-Minute Auto-Delete:</strong> All uploaded and processed files are automatically and permanently deleted from our servers after 30 minutes. This process is automated and cannot be overridden.
                </p>
                <p>
                  Once deleted, files cannot be recovered. Please ensure you download your processed files before they expire.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold font-['Outfit'] mb-4">4. Security</h2>
              <div className="prose prose-sm text-muted-foreground space-y-3">
                <p>
                  We implement appropriate technical measures to protect your files during upload, processing, and download. However, no method of transmission over the Internet is 100% secure.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold font-['Outfit'] mb-4">5. Your Rights</h2>
              <div className="prose prose-sm text-muted-foreground space-y-3">
                <p>Since we don't permanently store your data and don't require account creation, there is no personal data to access, correct, or delete. Your files are automatically removed after 30 minutes.</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold font-['Outfit'] mb-4">6. Contact Us</h2>
              <div className="prose prose-sm text-muted-foreground space-y-3">
                <p>
                  If you have any questions about this Privacy Policy, please contact us at:{" "}
                  <a href="mailto:twopearlsprivatelimited@gmail.com" className="text-[#FF3B30] hover:underline">
                    twopearlsprivatelimited@gmail.com
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default PrivacyPolicyPage;
